/**
 * usePaginatedData - Custom hook for server-side pagination
 *
 * Part of FlexFlow ERP Optimization - Phase 0: Foundation
 *
 * This hook provides:
 * - Server-side pagination (limit/offset)
 * - Debounced search (prevents excessive API calls)
 * - Server-side filtering
 * - Server-side sorting
 * - Loading and error states
 * - Automatic data refresh on parameter changes
 *
 * @example
 * // Basic usage
 * const {
 *   data, loading, error, pagination,
 *   handlePageChange, handleSearchChange, refresh
 * } = usePaginatedData(getOrdersPaginated, {
 *   initialPageSize: 50,
 *   initialFilters: { statusFilter: "PENDING" }
 * })
 *
 * @example
 * // With all options
 * const orders = usePaginatedData(getOrdersPaginated, {
 *   initialPageSize: 50,
 *   initialFilters: { statusFilter: "ALL" },
 *   debounceMs: 300,
 *   autoFetch: true,
 *   initialSort: { field: "created_at", direction: "DESC" }
 * })
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react"

/**
 * @typedef {Object} PaginationInfo
 * @property {number} page - Current page (0-indexed)
 * @property {number} pageSize - Records per page
 * @property {number} total - Total record count
 * @property {number} totalPages - Total number of pages
 * @property {boolean} hasNextPage - Can navigate forward
 * @property {boolean} hasPrevPage - Can navigate backward
 * @property {number} startIndex - First record index (1-indexed for display)
 * @property {number} endIndex - Last record index (for display)
 */

/**
 * @typedef {Object} SortModel
 * @property {string|null} field - Field to sort by
 * @property {string} direction - "ASC" or "DESC"
 */

/**
 * @typedef {Object} UsePaginatedDataOptions
 * @property {number} initialPageSize - Records per page (default: 50)
 * @property {Object} initialFilters - Initial filter values
 * @property {number} debounceMs - Search debounce delay (default: 300)
 * @property {boolean} autoFetch - Fetch on mount (default: true)
 * @property {SortModel} initialSort - Initial sort configuration
 */

/**
 * @typedef {Object} UsePaginatedDataReturn
 * @property {Array} data - Current page data
 * @property {number} total - Total record count
 * @property {boolean} loading - Loading state
 * @property {string|null} error - Error message if any
 * @property {PaginationInfo} pagination - Pagination metadata
 * @property {number} page - Current page (0-indexed)
 * @property {number} pageSize - Current page size
 * @property {string} searchTerm - Current search input (not debounced)
 * @property {Object} filters - Current filter state
 * @property {SortModel} sortModel - Current sort configuration
 * @property {Function} handlePageChange - Set page (0-indexed)
 * @property {Function} handlePageSizeChange - Set page size (resets to page 0)
 * @property {Function} handleSearchChange - Set search term (debounced)
 * @property {Function} handleFilterChange - Update filters (merges with existing)
 * @property {Function} handleSortChange - Set sort field and direction
 * @property {Function} refresh - Manually refetch current page
 * @property {Function} resetFilters - Reset all filters to initial values
 * @property {Function} setPage - Direct page setter
 * @property {Function} setPageSize - Direct page size setter
 * @property {Function} setFilters - Direct filters setter
 */

/**
 * Custom hook for managing server-side paginated data
 *
 * @param {Function} fetchFunction - Async function that returns { data, total, hasMore }
 * @param {UsePaginatedDataOptions} options - Configuration options
 * @returns {UsePaginatedDataReturn}
 */
export function usePaginatedData(fetchFunction, options = {}) {
  const {
    initialPageSize = 50,
    initialFilters = {},
    debounceMs = 300,
    autoFetch = true,
    initialSort = { field: null, direction: "DESC" }
  } = options

  // ============================================================================
  // STATE
  // ============================================================================

  // Data state
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Pagination state
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(initialPageSize)

  // Search state (with debouncing)
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")

  // Filter and sort state
  const [filters, setFilters] = useState(initialFilters)
  const [sortModel, setSortModel] = useState(initialSort)

  // Track if this is the initial mount
  const isInitialMount = useRef(true)
  const fetchIdRef = useRef(0) // For handling race conditions

  // ============================================================================
  // DEBOUNCED SEARCH
  // ============================================================================

  useEffect(() => {
    const timer = setTimeout(() => {
      // Only update if the value actually changed
      if (debouncedSearch !== searchTerm) {
        setDebouncedSearch(searchTerm)
        // Reset to first page when search changes
        setPage(0)
      }
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [searchTerm, debounceMs, debouncedSearch])

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchData = useCallback(async () => {
    // Increment fetch ID to handle race conditions
    const currentFetchId = ++fetchIdRef.current

    setLoading(true)
    setError(null)

    try {
      // Build request parameters
      const requestParams = {
        limit: pageSize,
        offset: page * pageSize,
        search: debouncedSearch || null,
        orderBy: sortModel.field,
        orderDir: sortModel.direction,
        ...filters
      }

      const result = await fetchFunction(requestParams)

      // Only update state if this is still the latest request
      if (currentFetchId === fetchIdRef.current) {
        setData(result.data || [])
        setTotal(result.total || 0)
      }
    } catch (err) {
      // Only update error if this is still the latest request
      if (currentFetchId === fetchIdRef.current) {
        const errorMessage = err.message || "Failed to fetch data"
        setError(errorMessage)
        setData([])
        setTotal(0)
        console.error("usePaginatedData fetch error:", err)
      }
    } finally {
      // Only clear loading if this is still the latest request
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false)
      }
    }
  }, [fetchFunction, page, pageSize, debouncedSearch, filters, sortModel])

  // Auto-fetch on dependency changes
  useEffect(() => {
    if (autoFetch) {
      // Skip initial fetch if autoFetch is true but we want to wait
      if (isInitialMount.current) {
        isInitialMount.current = false
      }
      fetchData()
    }
  }, [fetchData, autoFetch])

  // ============================================================================
  // HANDLERS
  // ============================================================================

  /**
   * Change current page (0-indexed)
   * @param {number} newPage - New page number
   */
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage)
  }, [])

  /**
   * Change page size and reset to first page
   * @param {number} newSize - New page size
   */
  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize)
    setPage(0)
  }, [])

  /**
   * Update search term (will be debounced before triggering fetch)
   * @param {string} term - Search term
   */
  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term)
  }, [])

  /**
   * Update filters (merges with existing filters) and reset to first page
   * @param {Object} newFilters - Filter values to merge
   */
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
    setPage(0)
  }, [])

  /**
   * Update sort configuration and reset to first page
   * @param {string} field - Field to sort by
   * @param {string} direction - "ASC" or "DESC" (defaults to "DESC")
   */
  const handleSortChange = useCallback((field, direction = "DESC") => {
    setSortModel({ field, direction })
    setPage(0)
  }, [])

  /**
   * Manually trigger a data refresh
   */
  const refresh = useCallback(() => {
    fetchData()
  }, [fetchData])

  /**
   * Reset all filters to initial values and clear search
   */
  const resetFilters = useCallback(() => {
    setFilters(initialFilters)
    setSearchTerm("")
    setDebouncedSearch("")
    setPage(0)
  }, [initialFilters])

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Pagination metadata for display and navigation
   */
  const pagination = useMemo(() => ({
    page,
    pageSize,
    total,
    totalPages: Math.ceil(total / pageSize) || 1,
    hasNextPage: (page + 1) * pageSize < total,
    hasPrevPage: page > 0,
    // Display-friendly indices (1-indexed)
    startIndex: total > 0 ? page * pageSize + 1 : 0,
    endIndex: Math.min((page + 1) * pageSize, total)
  }), [page, pageSize, total])

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // Data
    data,
    total,
    loading,
    error,

    // Pagination
    pagination,
    page,
    pageSize,

    // Search & Filters
    searchTerm,
    debouncedSearch,
    filters,
    sortModel,

    // Handlers
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
    handleFilterChange,
    handleSortChange,
    refresh,
    resetFilters,

    // Direct setters (for advanced use cases)
    setPage,
    setPageSize,
    setFilters,
    setSearchTerm
  }
}

export default usePaginatedData
